// Largely a copy of mapbox-gl-js's own supported function:
// https://github.com/mapbox/mapbox-gl-supported/blob/gh-pages/index.js
//
// But this function exports the actual results for why the support check
// failed so we can log the details to Rollbar for better debugging and
// understanding what features exactly aren't supported by specific browsers.
//
// The copy/paste of the bulk of this script could be removed if
// mapbox-gl-supported is updated to export these results in a similar fashion:
// https://github.com/mapbox/mapbox-gl-supported/issues/18

/* eslint-disable */

function isSupported(options) {
  return !!(
    isBrowser() &&
    isArraySupported() &&
    isFunctionSupported() &&
    isObjectSupported() &&
    isJSONSupported() &&
    isWorkerSupported() &&
    isUint8ClampedArraySupported() &&
    isArrayBufferSupported() &&
    isWebGLSupportedCached(options && options.failIfMajorPerformanceCaveat)
  );
}

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function isArraySupported() {
  return (
    Array.prototype &&
    Array.prototype.every &&
    Array.prototype.filter &&
    Array.prototype.forEach &&
    Array.prototype.indexOf &&
    Array.prototype.lastIndexOf &&
    Array.prototype.map &&
    Array.prototype.some &&
    Array.prototype.reduce &&
    Array.prototype.reduceRight &&
    Array.isArray
  );
}

function isFunctionSupported() {
  return Function.prototype && Function.prototype.bind;
}

function isObjectSupported() {
  return (
    Object.keys &&
    Object.create &&
    Object.getPrototypeOf &&
    Object.getOwnPropertyNames &&
    Object.isSealed &&
    Object.isFrozen &&
    Object.isExtensible &&
    Object.getOwnPropertyDescriptor &&
    Object.defineProperty &&
    Object.defineProperties &&
    Object.seal &&
    Object.freeze &&
    Object.preventExtensions
  );
}

function isJSONSupported() {
  return 'JSON' in window && 'parse' in JSON && 'stringify' in JSON;
}

function isWorkerSupported() {
  if (!('Worker' in window && 'Blob' in window && 'URL' in window)) {
    return false;
  }

  var blob = new Blob([''], { type: 'text/javascript' });
  var workerURL = URL.createObjectURL(blob);
  var supported;
  var worker;

  try {
    worker = new Worker(workerURL);
    supported = true;
  } catch (e) {
    supported = false;
  }

  if (worker) {
    worker.terminate();
  }
  URL.revokeObjectURL(workerURL);

  return supported;
}

// IE11 only supports `Uint8ClampedArray` as of version
// [KB2929437](https://support.microsoft.com/en-us/kb/2929437)
function isUint8ClampedArraySupported() {
  return 'Uint8ClampedArray' in window;
}

// https://github.com/mapbox/mapbox-gl-supported/issues/19
function isArrayBufferSupported() {
  return ArrayBuffer.isView;
}

var isWebGLSupportedCache = {};
function isWebGLSupportedCached(failIfMajorPerformanceCaveat) {

  if (isWebGLSupportedCache[failIfMajorPerformanceCaveat] === undefined) {
    isWebGLSupportedCache[failIfMajorPerformanceCaveat] = isWebGLSupported(failIfMajorPerformanceCaveat);
  }

  return isWebGLSupportedCache[failIfMajorPerformanceCaveat];
}

isSupported.webGLContextAttributes = {
  antialias: false,
  alpha: true,
  stencil: true,
  depth: true
};

function isWebGLSupported(failIfMajorPerformanceCaveat) {

  var canvas = document.createElement('canvas');

  var attributes = Object.create(isSupported.webGLContextAttributes);
  attributes.failIfMajorPerformanceCaveat = failIfMajorPerformanceCaveat;

  if (canvas.probablySupportsContext) {
    return (
      canvas.probablySupportsContext('webgl', attributes) ||
      canvas.probablySupportsContext('experimental-webgl', attributes)
    );

  } else if (canvas.supportsContext) {
    return (
      canvas.supportsContext('webgl', attributes) ||
      canvas.supportsContext('experimental-webgl', attributes)
    );

  } else {
    return (
      canvas.getContext('webgl', attributes) ||
      canvas.getContext('experimental-webgl', attributes)
    );
  }
}

/* eslint-enable */

export default function supportedDetails() {
  return {
    isBrowser: !!isBrowser(),
    isArraySupported: !!isArraySupported(),
    isFunctionSupported: !!isFunctionSupported(),
    isObjectSupported: !!isObjectSupported(),
    isJSONSupported: !!isJSONSupported(),
    isWorkerSupported: !!isWorkerSupported(),
    isUint8ClampedArraySupported: !!isUint8ClampedArraySupported(),
    isArrayBufferSupported: !!isArrayBufferSupported(),
    isWebGLSupportedCached: !!isWebGLSupportedCached(false),
    isWebGLSupportedCachedFailIfMajorPerformanceCaveat: !!isWebGLSupportedCached(true),
  };
}
